import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Users, Calendar, CheckCircle2, UserPlus, Trash2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useInternalLink } from "@/hooks/use-internal-link";
import type { EvaluationGroupProgress } from "@/types/evaluation";

interface EvaluationGroupCardProps {
  group: EvaluationGroupProgress;
  onClick: (group: EvaluationGroupProgress) => void;
  onAddFreelancers?: () => void;
  canDelete?: boolean;
  onDelete?: (group: EvaluationGroupProgress) => void;
}

export function EvaluationGroupCard({
  group,
  onClick,
  onAddFreelancers,
  canDelete = false,
  onDelete,
}: EvaluationGroupCardProps) {
  const linkProps = useInternalLink();
  const cardHref = `/avaliacoes/perfil-freelancer/${group.group_id}`;
  return (
    <Card
      className="cursor-pointer hover:border-primary transition-colors relative group"
      {...linkProps(cardHref, () => onClick(group))}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl font-bold line-clamp-1">
            {group.group_name}
          </CardTitle>
          <Badge variant={group.is_active ? "default" : "secondary"}>
            {group.is_active ? "Ativo" : "Inativo"}
          </Badge>
        </div>
        {group.group_description && (
          <CardDescription className="line-clamp-2">
            {group.group_description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>
              {format(new Date(group.created_at), "dd/MM/yyyy", {
                locale: ptBR,
              })}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>{group.total_freelancers} freelancers</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Progresso
            </span>
            <span className="font-medium">
              {group.freelancers_evaluated} / {group.total_freelancers} (
              {group.progress_percentage}%)
            </span>
          </div>
          <Progress value={group.progress_percentage} className="h-2" />
        </div>

        {group.evaluator_name && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Avaliador: <span className="font-medium">{group.evaluator_name}</span>
          </div>
        )}

        <div className="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {canDelete && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(group);
              }}
              title="Deletar Grupo"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {onAddFreelancers && (
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onAddFreelancers();
              }}
              title="Adicionar Freelancers"
            >
              <UserPlus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
